/**
 * Haven desktop navigation.
 *
 * Opens a top-level item's panel on hover intent or keyboard focus, keeps it open while the
 * pointer travels into the panel, and closes it on pointer leave, Escape or an outside click.
 * Panels size to their content in CSS, so nothing has to be measured or clipped.
 * When the links don't fit beside the logo and icons, the header switches to the drawer.
 */

import { isTouchDevice } from '@theme/utilities';

const OPEN_DELAY = 60;
const CLOSE_DELAY = 220;

/** Below this width the header always uses the drawer (set in CSS). */
const MENU_MIN_WIDTH = 750;

class HavenNav extends HTMLElement {
  /** @type {HTMLElement | null} */
  #openItem = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #openTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {string} */
  #lastPointerType = 'mouse';

  /** Set while focus is handed back to a top-level link after Escape, so it doesn't reopen. */
  #suppressFocusOpen = false;

  #imagesRequested = false;

  /** Window width at which the links last didn't fit, while the header shows the drawer instead. */
  #tooNarrowAt = 0;

  #resizeFrame = 0;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    for (const item of this.querySelectorAll('[data-haven-nav-item]')) {
      if (!(item instanceof HTMLElement)) continue;

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.#scheduleOpen(item);
      }, { signal });

      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        this.#scheduleClose();
      }, { signal });

      item.addEventListener('focusin', () => {
        if (!this.#suppressFocusOpen) this.#open(item);
      }, { signal });

      item.addEventListener('focusout', (event) => {
        if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;
        this.#scheduleClose(0);
      }, { signal });

      const link = item.querySelector(':scope > .haven-nav__link');

      // On touch, the first tap opens the panel and a second tap follows the link.
      link?.addEventListener('click', (event) => {
        if (this.#lastPointerType === 'touch' && this.#openItem !== item) {
          event.preventDefault();
          this.#open(item);
        }
      }, { signal });

      // Arrow Down opens the panel and moves focus to its first link.
      link?.addEventListener('keydown', (event) => {
        if (!(event instanceof KeyboardEvent) || event.key !== 'ArrowDown') return;
        event.preventDefault();
        this.#open(item);
        const firstLink = item.querySelector('.haven-nav__panel a[href]');
        if (firstLink instanceof HTMLElement) firstLink.focus();
      }, { signal });
    }

    this.addEventListener('pointerdown', (event) => {
      this.#lastPointerType = event.pointerType;
    }, { signal, capture: true });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.#openItem) return;
      const link = this.#openItem.querySelector(':scope > .haven-nav__link');
      this.#close();
      if (link instanceof HTMLElement) {
        this.#suppressFocusOpen = true;
        link.focus();
        this.#suppressFocusOpen = false;
      }
    }, { signal });

    document.addEventListener('pointerdown', (event) => {
      if (this.#openItem && event.target instanceof Node && !this.contains(event.target)) this.#close();
    }, { signal });

    window.addEventListener('resize', this.#handleResize, { signal });

    // Panel images are lazy and closed panels aren't rendered, so nothing downloads on page load.
    // Fetch them as soon as the pointer reaches the header (or focus enters the menu).
    const headerRow = this.closest('.header__row') ?? this;
    headerRow.addEventListener('pointerenter', () => this.#requestImages(), { signal, once: true });
    this.addEventListener('focusin', () => this.#requestImages(), { signal, once: true });

    // The header's inline script may already have switched to the drawer before this ran.
    if (this.hasAttribute('minimum-reached')) this.#tooNarrowAt = window.innerWidth;
    this.#updateMenuStyle();

    // Web fonts can widen the links after the first check.
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#updateMenuStyle();
    });

    this.classList.add('is-ready');
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = 0;
    this.#openItem = null;
  }

  #handleResize = () => {
    this.#close();
    if (this.#resizeFrame) return;

    this.#resizeFrame = requestAnimationFrame(() => {
      this.#resizeFrame = 0;
      this.#updateMenuStyle();
    });
  };

  /**
   * There's no overflow list here, so when the links don't fit the header switches to the drawer,
   * and switches back once the window is wider than when they last didn't fit.
   */
  #updateMenuStyle() {
    const header = this.closest('#header-component');
    if (!(header instanceof HTMLElement) || isTouchDevice() || window.innerWidth < MENU_MIN_WIDTH) return;
    if (this.hasAttribute('minimum-reached') && window.innerWidth <= this.#tooNarrowAt) return;

    this.removeAttribute('minimum-reached');
    if (header.dataset.menuStyle !== 'menu') header.dataset.menuStyle = 'menu';

    const fits = /** @type {any} */ (window).havenNavFits;
    if (typeof fits !== 'function' || fits(this)) return;

    this.setAttribute('minimum-reached', '');
    this.#tooNarrowAt = window.innerWidth;
    header.dataset.menuStyle = 'drawer';
  }

  #requestImages() {
    if (this.#imagesRequested) return;
    this.#imagesRequested = true;
    for (const image of this.querySelectorAll('.haven-nav__panel img[loading="lazy"]')) {
      if (image instanceof HTMLImageElement) image.loading = 'eager';
    }
  }

  /** @param {HTMLElement} item */
  #scheduleOpen(item) {
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#openTimer);

    // Moving between items while one is open switches straight away.
    if (this.#openItem) {
      this.#open(item);
      return;
    }

    this.#openTimer = setTimeout(() => this.#open(item), OPEN_DELAY);
  }

  #scheduleClose(delay = CLOSE_DELAY) {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.#close(), delay);
  }

  /** @param {HTMLElement} item */
  #open(item) {
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#openTimer);
    if (this.#openItem === item) return;

    this.#close();
    this.#closeLocalization();
    this.#setPanelTop();
    this.#openItem = item;
    item.classList.add('is-open');
    item.querySelector(':scope > .haven-nav__link')?.setAttribute('aria-expanded', 'true');
  }

  /** An open currency and language dropdown would otherwise sit on top of the panel. */
  #closeLocalization() {
    const header = this.closest('#header-component') ?? document;
    for (const dropdown of header.querySelectorAll('dropdown-localization-component')) {
      if ('hidePanel' in dropdown && typeof dropdown.hidePanel === 'function') dropdown.hidePanel();
    }
  }

  #close() {
    clearTimeout(this.#openTimer);
    const item = this.#openItem;
    if (!item) return;

    item.classList.remove('is-open');
    item.querySelector(':scope > .haven-nav__link')?.setAttribute('aria-expanded', 'false');
    this.#openItem = null;
  }

  /** Lets the panel's max-height fit the space left below the header. */
  #setPanelTop() {
    const row = this.closest('.header__row') ?? this;
    const bottom = Math.max(0, Math.round(row.getBoundingClientRect().bottom));
    this.style.setProperty('--haven-nav-top', `${bottom}px`);
  }
}

if (!customElements.get('haven-nav')) {
  customElements.define('haven-nav', HavenNav);
}
